<script setup lang="ts">
  import { capitalize, computed, onBeforeMount, ref } from 'vue';

  import { IconColoredDeal } from '@/common/icons/colored';
  import { IconEnlarge } from '@/common/icons/monochrome';
  import { Package } from '@/common/models';
  import { CurationType } from '@/common/models/package/CurationType.enum';
  import DealIDType from '@/common/models/package/DealIDType.enum';
  import { i } from '@/globals';
  import LineItemDeal from '@/org-app/components/lineItems/LineItemDeal.vue';
  import { useExternalSSPs } from '@/org-app/services/tanstack/queries';
  import { waitForPendingQueries } from '@/org-app/services/tanstack/queryUtils';
  import i18n from '@/vueI18n';

  interface IProps {
    deal: Package;
  }

  const { deal } = defineProps<IProps>();

  const MIN_DEALS_TO_SHOW = 3;

  const externalDeals = ref([]);
  const isExpanded = ref(false);

  const externalSSPsQuery = useExternalSSPs();

  const externalSSPs = computed(() => externalSSPsQuery.items.value);
  const hasExternalSSPs = computed(() => deal.isCuratedDeal && deal.curationType === CurationType.EXTERNAL_SSP_DEALS);
  const visibleDeals = computed(() => {
    return isExpanded.value ? externalDeals.value : externalDeals.value.slice(0, 3);
  });
  const totalAdditionalDeals = computed(() => {
    if (externalDeals.value.length > MIN_DEALS_TO_SHOW) {
      return externalDeals.value.length - MIN_DEALS_TO_SHOW;
    }
    return 0;
  });
  const displayAllButtonLabel = computed<string>(() => {
    return visibleDeals.value.length === externalDeals.value.length
      ? i18n.global.t('actions.showLess')
      : `${i18n.global.t('actions.showAll')} (+${totalAdditionalDeals.value})`;
  });

  function toggleExpanded() {
    isExpanded.value = !isExpanded.value;
  }

  onBeforeMount(async () => {
    if (hasExternalSSPs.value) {
      await waitForPendingQueries([externalSSPsQuery]);
      externalDeals.value = Object.entries(JSON.parse(deal.dealIDsBySspProperty.value))
        .map(([ssp, dealIDs]) => {
          const externalSSP = externalSSPs.value.find((x) => x.id === Number(ssp));
          return (dealIDs as number[]).map((dealID) => {
            return Package.fromObject({
              id: -1,
              organizationID: Number(ssp),
              dealID: `${externalSSP.name} - ${dealID}`,
            });
          });
        })
        .flat();
    }
  });
</script>

<template>
  <template v-if="externalDeals.length > 0">
    <div class="row">
      <div class="col-12">
        <div class="pane-header">
          <span class="icon"><IconColoredDeal /></span>
          <span>{{ capitalize(i('names.deal', 2)) }}</span>
        </div>
        <div class="row">
          <div class="col-12">
            <div class="pane-content pad-top flex flex-column">
              <!-- Re-used this component since it involved the exact same data/styling, didn't use the overlying line item deals component since there was a bunch of differing logic there. -->
              <div
                class="deals-grid"
                :class="{
                  'deals-grid-single-deal': visibleDeals.length === 1,
                  'deals-grid-two-deals': visibleDeals.length === 2,
                  'deals-grid-more-deals': visibleDeals.length >= MIN_DEALS_TO_SHOW,
                }">
                <line-item-deal
                  v-for="externalDeal in visibleDeals"
                  :key="externalDeal.dealID"
                  :deal="externalDeal"
                  :package-id="externalDeal.id"
                  :type="DealIDType.External" />
              </div>
              <div
                v-if="totalAdditionalDeals > 0"
                class="more">
                <button
                  class="toggle"
                  @click="toggleExpanded">
                  <IconEnlarge class="svg" />
                  <span>{{ displayAllButtonLabel }}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </template>
</template>

<style scoped lang="scss">
  @use '@/styles/variables' as *;
  .deals-grid {
    display: grid;
    gap: 1rem;
  }

  .deals-grid-single-deal {
    grid-template-columns: 1fr;
  }

  .deals-grid-two-deals {
    grid-template-columns: repeat(2, 1fr);
  }

  .deals-grid-more-deals {
    grid-template-columns: repeat(3, 1fr);
  }

  .more {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 1.5rem;

    button.toggle {
      color: var(--secondaryColor);
      font-weight: var(--buttonFontWeight);
      border: none;
      transition: all 0.5s cubic-bezier(0.2, 1, 0.2, 1);
      cursor: pointer;
      display: flex;
      flex-direction: row;
      align-items: center;
      background: white;
      outline: none;
      font-size: 0.7em;
      text-transform: uppercase;
      border-radius: 1.9rem;
      padding: 0.4rem 0.8rem 0.4rem 0.5rem;
      margin: 0 auto;

      &:hover {
        transition: all 0.5s cubic-bezier(0.2, 1, 0.2, 1);
        background: var(--secondaryColor);
        color: var(--secondaryColorReverse);

        svg {
          stroke: var(--secondaryColorReverse);
          fill: var(--secondaryColorReverse);
        }
      }

      &.toggled {
        svg {
          transform: rotate(180deg);
        }
      }

      svg {
        transition: all 0.5s cubic-bezier(0.2, 1, 0.2, 1);
        height: 1.8rem;
        width: 1.8rem;
        stroke: var(--secondaryColor);
        fill: var(--secondaryColor);
      }

      span {
        margin-left: 0.6rem;
      }

      &.expanded {
        svg {
          transition: all 0.5s cubic-bezier(0.2, 1, 0.2, 1);
          transform: rotateX(180deg);
        }
      }
    }
  }
</style>
